import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { chartTheme, getSeriesColor } from "./theme";

interface DataPoint {
  date: string;
  [key: string]: string | number;
}

interface Series {
  key: string;
  name: string;
  color?: string;
}

interface LineChartProps {
  data: DataPoint[];
  series: Series[];
  xAxisKey?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  formatValue?: (value: number) => string;
  formatDate?: (date: string) => string;
  /** Least space between x axis labels, in px; Recharts skips labels to keep it. */
  minTickGap?: number;
}

export function LineChart({
  data,
  series,
  xAxisKey = "date",
  height = 300,
  showGrid = true,
  showLegend = true,
  formatValue = (v) => v.toLocaleString(),
  formatDate = (d) =>
    new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  minTickGap,
}: LineChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsLineChart
        data={data}
        // Room on the right so the last x label isn't clipped
        margin={{ top: 10, right: 24, left: 0, bottom: 0 }}
      >
        {showGrid && (
          <CartesianGrid
            strokeDasharray={chartTheme.grid.strokeDasharray}
            stroke={chartTheme.grid.stroke}
            vertical={false}
          />
        )}
        <XAxis
          dataKey={xAxisKey}
          axisLine={{ stroke: chartTheme.axis.stroke }}
          tickLine={false}
          tick={chartTheme.axis.tick}
          tickFormatter={formatDate}
          minTickGap={minTickGap}
          dy={10}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={chartTheme.axis.tick}
          tickFormatter={formatValue}
          dx={-10}
        />
        <Tooltip
          contentStyle={chartTheme.tooltip.contentStyle}
          labelStyle={chartTheme.tooltip.labelStyle}
          formatter={(value, name) => [formatValue(Number(value)), String(name)]}
          labelFormatter={(label) => formatDate(String(label))}
        />
        {showLegend && <Legend wrapperStyle={chartTheme.legend.wrapperStyle} />}
        {series.map((s, index) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color || getSeriesColor(index)}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}
